import cookie from '@s-ui/js/lib/cookie'
import {getDecision} from '@s-ui/pde'
import {FeedbackList} from '@adv-mt/frontend-ma--lib-trust'

import {CURRENT_YEAR, getHasPriceWithinFinancingRange, MAX_YEARS_OLD} from './variants/legacy/utils.js'
import {FOUND_STATUS_CODE, NO_CACHE_HEADERS} from './config.js'

const DEFAULT_EMPTY_ITEMS_RESPONSE = {items: []}
const PROMISE_REJECTED = 'rejected'
const REQUIRED_NEWS_YEAR = 2000
const NEWS_FETCH_COUNT = 8

// The trust API scores a review from 0 to 1, while the dealer average and the stars the panel
// renders both work on a 0 to 5 scale.
const REVIEW_SCORE_SCALE = 5

// Both the panel and the drawer read the reviews newest first — the panel re-sorts its own preview.
const byNewestReview = (a, b) => new Date(b.givenAt) - new Date(a.givenAt)

// A review with no score cannot be rendered as stars, so it is not displayable at all.
const hasReviewScore = ({feedback}) => typeof feedback.score === 'number'

// Flattens a trust API entry into the shape the ratings panel and drawer render. A reply with no
// text is dropped as if the dealer had never answered.
const toDealerReview = ({feedback: {feedbackId, givenAt, options = [], reply, score, textReview}, name}) => ({
  feedbackId,
  givenAt,
  name,
  reply: reply?.text ? {authorName: reply.authorName, givenAt: reply.givenAt, text: reply.text} : null,
  score: score * REVIEW_SCORE_SCALE,
  tags: options.filter(({selected}) => selected).map(({title}) => title),
  textReview
})

const ASPECT_RATIO = 1.33
const DESKTOP_WIDTH = 712
const MOBILE_WIDTH = 560

const getParsedDealerAdsResponse = ({sellerAdId, dealerAdsResponse, isAdDetailRevampEnabled}) => {
  const INITIAL_SELLER_ADS_LIST = {items: [], totalResults: 0}
  const VEHICLE_ITEMS_FROM = 0
  const VEHICLE_ITEMS_TO_LEGACY = 4
  const VEHICLE_ITEMS_TO_REVAMP = 10
  const VEHICLE_ITEMS_TO = isAdDetailRevampEnabled ? VEHICLE_ITEMS_TO_REVAMP : VEHICLE_ITEMS_TO_LEGACY

  const filterBy =
    adId =>
    ({id}) =>
      String(id) !== String(adId)
  const filterSellerAdsListById = filterBy(sellerAdId)

  const [error, response] = dealerAdsResponse
  if (error) return INITIAL_SELLER_ADS_LIST
  const {items, totalResults} = response
  const ads = items.filter(filterSellerAdsListById).slice(VEHICLE_ITEMS_FROM, VEHICLE_ITEMS_TO)

  return {
    items: ads,
    totalResults
  }
}

const getAdDetailsUrlToRedirect = ({adId, pathPattern}) => pathPattern.replace(':id', adId)

export const getInitialProps = async ({routeInfo, context: {cookies, domain, i18n, user, pde}}) => {
  const {
    params,
    location: {pathname: path, query, search: queryParams, state}
  } = routeInfo

  const {provincesTracking} = query
  const {provinces} = cookie.parse(cookies)

  const multiProvinceTrackingData = provinces || provincesTracking || (state && state.provinces)
  const {favoriteTrackingOrigin} = state || {}
  const origin = state?.origin

  const videoPlay = state?.videoPlay === true
  const videoTime = state?.videoTime

  const config = domain.get('config')
  const {REFERER} = config.get('REQUEST_HEADERS')
  const referer = config.get(REFERER)
  const URL_PROTOCOL = config.get('URL_PROTOCOL')
  const SECURED_ROOT_SUBDOMAIN = config.get('SECURED_ROOT_SUBDOMAIN')
  const HOST_NAME_DOMAIN = config.get('HOST_NAME_DOMAIN')
  const isMobile = domain.get('isMobile')
  const {offerTypes, OFFER_TYPE_SUBSCRIPTION} = config.get('search')
  const {HAS_FINANCING_PRI_ENABLED} = config.get('financing')
  const {TARGET_BUYER_IDS} = config.get('subscription')
  const {GetAdDetailsPartialContentError} = config.get('COMMON').ERRORS
  const {RECOMMENDED_ADS} = config.get('COMMON').QUERY_PARAMS
  const urlSite = `${URL_PROTOCOL}${SECURED_ROOT_SUBDOMAIN}${HOST_NAME_DOMAIN}`

  const {decide} = getDecision(pde)

  const {
    PAGE_PATTERNS,
    ADS_LIST_PAGE_NAME,
    ADS_LIST_BUSINESS_SUBSCRIPTION_PAGE_NAME,
    ADS_DETAIL_PREVIEW_PAGE_NAME,
    ADS_DETAIL_PATH_PATTERN
  } = config.get('page')

  const {CREATE_ALERT_QUERY_PARAM, AD_STATUS} = config.get('search')

  const {DEALER_PACKS} = config.get('dealer')

  const adsListUrl = PAGE_PATTERNS[ADS_LIST_PAGE_NAME][0]

  const getPageName = () => domain.get('get_page_name_page_use_case').execute({path})

  const [
    {
      value: {pageName}
    }
  ] = await Promise.allSettled([getPageName()])

  const isPreview = pageName === ADS_DETAIL_PREVIEW_PAGE_NAME

  const {enabled: isAdDetailRevampEnabled} = decide('ff_web_buyer_ad_detail_revamp', {
    queryString: queryParams
  })

  const _getAdDetailResponse = () =>
    domain.get('get_ad_details_ads_use_case').execute({adId: params.adId, isPreview, expand: RECOMMENDED_ADS})

  const _getSearchData = () =>
    domain.get('get_search_data_from_url_search_use_case').execute({path, query, url: path + queryParams})

  const getCanonicalPathname = async () => {
    const [searchDataError, searchDataResponse] = await _getSearchData()
    if (searchDataError) return adsListUrl
    return searchDataResponse?.getCanonicalPathname() || adsListUrl
  }

  const [
    {
      value: [adDetailsError, adDetailsResponse]
    }
  ] = await Promise.allSettled([_getAdDetailResponse()])

  if (adDetailsError) {
    // Log handled errors in Mushroom.
    if (adDetailsError.origin) console.error(adDetailsError) //eslint-disable-line
    console.error(JSON.stringify({message: 'ad does not exist', path, from: state?.from || referer}))

    const hasPublishedAdId = Boolean(adDetailsError.adId)
    const isAdDetailsPartialContentError = adDetailsError instanceof GetAdDetailsPartialContentError
    const shouldRedirectToAdDetailsPage = isAdDetailsPartialContentError && hasPublishedAdId

    let redirectTo
    if (shouldRedirectToAdDetailsPage) {
      redirectTo = getAdDetailsUrlToRedirect({
        adId: adDetailsError.adId,
        pathPattern: ADS_DETAIL_PATH_PATTERN
      })
    } else {
      redirectTo = await getCanonicalPathname()
    }

    return {
      __HTTP__: {
        headers: NO_CACHE_HEADERS,
        redirectStatusCode: FOUND_STATUS_CODE,
        redirectTo
      }
    }
  }

  const {
    ad = {},
    adStats,
    adType,
    financingData,
    isTradable,
    recommendedAds,
    isRecommendedAdsFallback,
    reservation = {},
    seller = {},
    status,
    vehicleCertification = {},
    vehicleHistory,
    vehicleInfo
  } = adDetailsResponse || {}

  const isRecommendedAdsFallbackValue = Boolean(isRecommendedAdsFallback?.value)

  const hasRentingForBusinessAdInfo = Boolean(ad.subscriptionBusinessAdInfo)
  const adRentingForBusiness = hasRentingForBusinessAdInfo && {
    ...ad,
    ...ad.subscriptionBusinessAdInfo,
    id: params.adId
  }

  const isOriginRentingBusiness = origin === ADS_LIST_BUSINESS_SUBSCRIPTION_PAGE_NAME
  const targetBuyer = isOriginRentingBusiness
    ? adRentingForBusiness.targetBuyer
    : ad.targetBuyer || TARGET_BUYER_IDS.PRIVATES

  const {contractId: sellerContractId, dealer = {}} = seller
  const {hasReservation, pack: dealerPackType} = dealer
  const hasDealerPack = Boolean(dealerPackType)
  const isDealerWithPackAdvance = seller.isDealer && hasDealerPack && dealerPackType === DEALER_PACKS.ADVANCE
  const isDealerWithPackExpert = seller.isDealer && hasDealerPack && dealerPackType === DEALER_PACKS.EXPERT
  const isDealerSubscription = seller.isDealer && dealerPackType === DEALER_PACKS.SUBSCRIPTION

  const {INACTIVE: AD_STATUS_INACTIVE} = AD_STATUS
  const isInactiveAd = status === AD_STATUS_INACTIVE

  if (isInactiveAd) {
    const getRedirectUrl = async () => {
      const isRenting = adType?.offerTypeId === OFFER_TYPE_SUBSCRIPTION
      /**
       * Renting is not fully implemented in semantics hence, the get_ads_dynamic_breadcrumbs_breadcrumb_use_case will return segunda-mano, so we get the canonical url which does work
       * https://jira.ets.mpi-internal.com/browse/MTR-85238
       * */
      if (isRenting) return getCanonicalPathname()

      const [, redirectUrls = []] = await domain.get('get_ads_dynamic_breadcrumbs_breadcrumb_use_case').execute({
        pageName,
        params: {
          ...adType,
          makeId: ad.makeId,
          modelId: ad.modelId,
          provinceId: ad.provinceId,
          version: ad.version || ad.title
        }
      })

      const [{url: baseUrl} = {}, {url: makeUrl} = {}, {url: makeModelUrl} = {}] = redirectUrls

      const redirectUrl = makeModelUrl || makeUrl || baseUrl || adsListUrl

      return redirectUrl.replace(urlSite, '')
    }
    // If the ad is not active, we redirect the user to the
    // ads-list page with a message saying the ad is not available anymore
    console.error(`inactive ad adId: ${ad.id} path: ${path} from: ${state?.from || referer}`)

    return {
      __HTTP__: {
        headers: NO_CACHE_HEADERS,
        httpCookie: {AdNotAvailable: true},
        redirectStatusCode: FOUND_STATUS_CODE,
        redirectTo: await getRedirectUrl()
      }
    }
  }

  const {
    specifications: {make, model, bodyType, cubicCapacity}
  } = vehicleInfo
  const {
    fuelType,
    id: adId,
    km: kms,
    makeId,
    modelId,
    price,
    priceRankIndicator,
    provinceId,
    subscriptionVehicleState,
    title,
    version,
    versionId,
    year
  } = ad

  const getOptimizedImageSrc = ({src}) => {
    const width = isMobile ? MOBILE_WIDTH : DESKTOP_WIDTH
    const aspectRatioHeight = Math.floor(width / ASPECT_RATIO)
    const size = `${width}x${aspectRatioHeight}`
    return `${src}/${size}cut/`
  }

  const imageSrc = Boolean(ad.img) && getOptimizedImageSrc({src: ad.img})

  const hasVersionId = Boolean(versionId)
  const hasRequiredYearToShowNews = year > REQUIRED_NEWS_YEAR

  const [, alertSearch] = await domain.get('get_alert_search_ads_use_case').execute({ad, params: adType})

  const alertSearchFilters = alertSearch.getSearchForFilters()

  /* Page params resolution */
  const _maybeCreateAlert = () => {
    return query[CREATE_ALERT_QUERY_PARAM]
      ? domain.get('create_alert_user_alert_use_case').execute({search: alertSearchFilters, adId})
      : Promise.resolve()
  }

  const _getBreadcrumbs = () =>
    domain.get('get_ads_dynamic_breadcrumbs_breadcrumb_use_case').execute({
      pageName,
      params: {
        ...adType,
        makeId,
        modelId,
        provinceId,
        version: version || title
      }
    })

  const _getWdylTagclouds = ({originUrl}) => {
    try {
      return domain.get('get_tagcloud_seo_use_case').execute({
        makeId,
        modelId,
        pageName,
        provinceId,
        context: 'wdyl',
        originUrl
      })
    } catch (error) {
      console.error(error) // eslint-disable-line
    }
  }

  const _getStockTagclouds = () =>
    seller.isPrivate
      ? domain.get('get_tagcloud_seo_use_case').execute({
          makeId,
          modelId,
          provinceId,
          context: 'stock'
        })
      : Promise.resolve()

  const _getNews = () =>
    hasVersionId && hasRequiredYearToShowNews
      ? domain.get('get_vehicle_news_use_case').execute({count: NEWS_FETCH_COUNT, make, model, year})
      : Promise.resolve([null, DEFAULT_EMPTY_ITEMS_RESPONSE])

  const _getDealerAds = () => {
    if (isDealerWithPackAdvance || isDealerWithPackExpert) {
      return domain.get('get_search_results_ads_use_case').execute({
        contractId: sellerContractId
      })
    }

    if (isDealerSubscription) {
      return domain.get('get_search_results_ads_use_case').execute({
        contractId: sellerContractId,
        // tech-debt, should be removed shortly
        sortBy: 'monthlySubscriptionFee',
        sortOrder: 'ASC'
      })
    }
    return Promise.resolve([null, DEFAULT_EMPTY_ITEMS_RESPONSE])
  }

  // Only the revamp renders the dealer ratings panel, and only dealers have trust reviews
  const _getDealerReviews = async () => {
    if (!isAdDetailRevampEnabled || !seller.isDealer || !sellerContractId) return [null, []]

    const {SDRN_TRUST_BASE} = config.get('ratings')

    const feedbackList = new FeedbackList({
      appName: config.get('APP_NAME'),
      baseUrl: `${config.get('API_URL')}${config.get('TRUST_API_PATH')}`,
      subjectId: `${SDRN_TRUST_BASE}${sellerContractId}`
    })

    try {
      const reviews = await feedbackList.getUserReviews()

      return [null, reviews.filter(hasReviewScore).map(toDealerReview).sort(byNewestReview)]
    } catch (error) {
      // The trust SDK throws on any failure, and a dealer's reviews are not worth a 500 on the
      // whole ad page — the panel already hides itself when the list comes back empty.
      return [error, []]
    }
  }

  const _getUserFavoriteAds = async () => {
    const favorites = await domain.get('get_user_favorites_use_case').execute()

    return {
      ads: favorites.ads,
      dealers: favorites.contracts
    }
  }

  const getIsSellerBlocked = async () => {
    const isSellerBlocked = await domain.get('parse_blocked_user_cookies_use_case').execute()

    return isSellerBlocked?.isUserBlocked?.(String(seller.dealer.sellerMessagingId))
  }

  const [
    {value: [breadcrumbError, breadcrumbResponse] = []},
    {value: dealerAdsResponse},
    {
      value: [newsFeedError, newsFeed = DEFAULT_EMPTY_ITEMS_RESPONSE]
    },
    {value: stockTagclouds},
    {status: maybeCreateAlertStatus, value: createdAlert},
    {value: isSellerBlocked},
    {value: [dealerReviewsError, dealerReviews = []] = []}
  ] = await Promise.allSettled([
    _getBreadcrumbs(),
    _getDealerAds(),
    _getNews(),
    _getStockTagclouds(),
    _maybeCreateAlert(),
    getIsSellerBlocked(),
    _getDealerReviews()
  ])

  const _getSeoData = () => {
    const offerTypeLiteral = offerTypes.find(({id}) => id === adType.offerTypeId)

    const params = {
      bodyType: i18n.t(bodyType), // TODO: check how to get bodyTypeIds MTR-56077
      breadcrumb: breadcrumbResponse,
      canonical: ad.url,
      color: i18n.t(ad.color),
      ...(cubicCapacity && {cc: i18n.n(cubicCapacity)}),
      ...(kms && {kms: i18n.n(kms)}),
      fuelType,
      image: ad.img,
      make,
      model,
      ...(offerTypeLiteral && {offerType: i18n.t(offerTypeLiteral.label)}),
      ...(ad.monthlySubscriptionFee && {
        monthlySubscriptionFee: i18n.t('COMMON.MONTHLY_FEE', {
          amount: i18n.c(ad.monthlySubscriptionFee)
        })
      }),
      ...(ad.subscriptionVehicleState && {subscriptionVehicleState}),
      offerTypeId: adType?.offerTypeId,
      price: i18n.n(ad.price),
      province: ad.province,
      title,
      version: vehicleInfo.specifications.version,
      year: vehicleInfo.year,
      adId: ad.id,
      photos: ad.photos,
      km: ad.km,
      hp: vehicleInfo.specifications.hp,
      doors: vehicleInfo.specifications.doors,
      seats: vehicleInfo.specifications.seats,
      fuelTankCapacity: vehicleInfo.specifications.fuelTankCapacity,
      consumptionAverage: vehicleInfo.specifications.consumptionAverage,
      emissions: vehicleInfo.specifications.emissions,
      transmissionType: ad.transmissionType,
      warrantyMonths: ad.warrantyMonths,
      hasOfficialWarranty: ad.hasOfficialWarranty,
      hasWarranty: ad.hasWarranty,
      sellerDealer: seller?.dealer,
      adDescription: ad.description,
      environmentalLabel: ad.environmentalLabel
    }

    return domain.get('get_seo_data_use_case').execute({
      culture: i18n.culture,
      pageName,
      make,
      params,
      preloadImages: [imageSrc]
    })
  }

  const createAlertError = maybeCreateAlertStatus === PROMISE_REJECTED
  const breadcrumb = !breadcrumbError ? breadcrumbResponse : []

  const [, {url: originUrl} = {}] = breadcrumb.slice().reverse() || []

  const hasPriceWithinFinancingRange = getHasPriceWithinFinancingRange(price)
  const hasVehicleYearWithinFinancingRange = CURRENT_YEAR - year <= MAX_YEARS_OLD
  const showC2cFinancing =
    HAS_FINANCING_PRI_ENABLED && seller.isPrivate && hasPriceWithinFinancingRange && hasVehicleYearWithinFinancingRange

  const hasFinancingData = Boolean(financingData?.id)
  const hasFdaProductActive = hasFinancingData && financingData.loanRequestAvailable && seller.isDealer

  const _getCurrentFinancingInfo = () => {
    const entry = state?.financingData?.entry || null
    const terms = state?.financingData?.terms || null
    return seller?.isDealer && (entry !== null || terms !== null)
      ? domain.get('get_financing_data_ads_use_case').execute({adId, entry, terms})
      : Promise.resolve([])
  }

  const _getAdReservationStatusNewFlow = () =>
    hasReservation
      ? domain.get('get_ad_reservation_status_transactions_use_case').execute({
          adId: ad.id
        })
      : Promise.resolve([])

  const _getGroupedSpecifications = () =>
    vehicleInfo.uniqueVehicleId
      ? domain.get('get_vehicle_info_vehicles_use_case').execute({
          pageName,
          grouped: true,
          vehicleId: vehicleInfo.uniqueVehicleId
        })
      : Promise.resolve([null, null])

  const [
    {
      value: [seoDataResponseError, seoDataResponse]
    },
    {value: wdylTagclouds},
    {
      value: [currentFinancingInfoError, currentFinancingInfoResponse]
    },
    {value: userFavoriteAds},
    {
      value: [, adReservationStatusNewFlowResponse]
    },
    {
      value: [, groupedSpecsResponse]
    }
  ] = await Promise.allSettled([
    _getSeoData(),
    _getWdylTagclouds({originUrl}),
    _getCurrentFinancingInfo(),
    _getUserFavoriteAds(),
    _getAdReservationStatusNewFlow(),
    _getGroupedSpecifications()
  ])

  const favoriteAds = userFavoriteAds?.ads || []
  const favoriteDealers = userFavoriteAds?.dealers || []

  const dealerFavoriteInfo = seller.isDealer
    ? favoriteDealers?.find(element => Number(element.id) === Number(seller.dealer?.id))
    : {}
  const adFavoriteInfo = favoriteAds?.find(element => Number(element.id) === Number(ad?.id))
  const priceDropData = adFavoriteInfo?.priceDropData

  // The recommender mappers never produce `isFavorite`, so the minicard hearts would render unmarked
  // on load even for ads the user already favorited. The user's favorites are already resolved above.
  const favoriteAdIds = new Set(favoriteAds.map(({id}) => String(id)))
  const withFavoriteFlag = ads => ads.map(item => ({...item, isFavorite: favoriteAdIds.has(String(item.id))}))

  const dealerAds = getParsedDealerAdsResponse({sellerAdId: adId, dealerAdsResponse, isAdDetailRevampEnabled})

  const _getPageTrackingData = () =>
    domain.get('get_page_tracking_data_use_case').execute({
      page: pageName,
      adDetail: {
        ad: isOriginRentingBusiness ? adRentingForBusiness : ad,
        adType,
        filtersString: state?.filters,
        filteredProvinces: multiProvinceTrackingData,
        financingInstalmentPrice: financingData?.fee || undefined,
        isTradableaAd: isTradable?.value,
        priceDropData,
        product: state?.product,
        seller,
        transactionInfo: {
          hasOnlineFinancing: financingData?.loanRequestAvailable || undefined,
          reservationStatus:
            (seller.isDealer && adReservationStatusNewFlowResponse?.value?.adReservationStatus) || undefined
        },
        vehicleInfo
      }
    })

  const _getLeadData = () =>
    domain.get('get_lead_data_tracking_use_case').execute({
      ad: isOriginRentingBusiness ? adRentingForBusiness : ad,
      adType,
      dealerId: sellerContractId,
      filtersString: state?.filters,
      financingInstalmentPrice: financingData?.fee || undefined,
      filters: {provinces: multiProvinceTrackingData},
      product: state?.product,
      transactionInfo: {
        hasOnlineFinancing: financingData?.loanRequestAvailable || undefined,
        reservationStatus:
          (seller.isDealer && adReservationStatusNewFlowResponse?.value?.adReservationStatus) || undefined
      },
      pageType: 'detail',
      pageNameOrigin: state?.page_name_origin,
      hasPricedrop: state?.has_pricedrop
    })

  const [
    {status: getPageTrackingDataStatus, value: pageTrackingDataResponse},
    {
      value: [leadDataError, leadData]
    }
  ] = await Promise.allSettled([_getPageTrackingData(), _getLeadData()])

  const pageTrackingDataError = getPageTrackingDataStatus === PROMISE_REJECTED

  // When get_lead_data_tracking_use_case fails, provide a minimal fallback so seller_type
  // is always present in Lead Triggered events. Other fields will be absent — intentional,
  // since the use-case failure already lost that data. `seller` is always defined at this point.
  const {SELLER_TYPES_TRANSLATIONS} = config.get('tracking')
  const {SELLER_TYPE_PROFESSIONAL, SELLER_TYPE_PRIVATE} = config.get('dealer')
  const culture = config.get('DEFAULT_CULTURE')
  const leadDataWithFallback = leadData ?? {
    seller_type: seller.isDealer
      ? SELLER_TYPES_TRANSLATIONS[SELLER_TYPE_PROFESSIONAL][culture]
      : SELLER_TYPES_TRANSLATIONS[SELLER_TYPE_PRIVATE][culture]
  }

  const showB2cFinancingPanel = hasFdaProductActive

  const seoDataError = Boolean(seoDataResponseError)

  const getStructuredData = ({scripts}) => scripts.map(script => JSON.parse(script.innerHTML))

  const seoData = seoDataResponse || {}
  const {scripts: structuredDataScripts = []} = seoData.head
  const structuredDataJsonList = getStructuredData({
    scripts: structuredDataScripts
  })
  const helmet = {...seoData, scripts: structuredDataJsonList}

  const showPriceIndicator = Boolean(priceRankIndicator)

  const currentFinancingData =
    seller?.isDealer && currentFinancingInfoError === null && Object.keys(state?.financingData).length > 0
      ? currentFinancingInfoResponse
      : financingData

  const hasAdReservationStatusNewFlowResponse =
    adReservationStatusNewFlowResponse && Object.keys(adReservationStatusNewFlowResponse.value).length > 0

  const groupedSpecifications = groupedSpecsResponse?.vehicleInfo?.groupedSpecifications ?? null

  return {
    isAdDetailRevampEnabled,
    ad: {...ad, id: params.adId},
    adRentingForBusiness,
    adFavoriteInfo,
    dealerFavoriteInfo,
    adStats,
    adType,
    alertSearch: alertSearchFilters,
    breadcrumb,
    createdAlert,
    currentUser: user,
    dealerAds: {...dealerAds, items: withFavoriteFlag(dealerAds.items)},
    dealerReviews,
    extendedTrackingData: {
      filters: state?.filters,
      provinces: state?.provinces,
      product: state?.product,
      ...(state?.pageNameOrigin && {pageNameOrigin: state.pageNameOrigin}),
      ...(state?.listType && {listType: state.listType}),
      ...(state?.hasPricedrop && {hasPricedrop: state.hasPricedrop})
    },
    favoriteTrackingOrigin,
    financingData: {
      ...currentFinancingData,
      cashPrice: price,
      financedPrice: ad.financedPrice
    },
    helmet,
    isPreview,
    isRecommendedAdsFallbackValue,
    leadData: leadDataWithFallback,
    multiProvinceTrackingData,
    newsFeed,
    reservationNewFlow: hasAdReservationStatusNewFlowResponse ? adReservationStatusNewFlowResponse.value : reservation,
    origin,
    pageName,
    pageErrors: {
      breadcrumbError,
      createAlertError,
      dealerReviewsError,
      leadDataError,
      newsFeedError,
      pageTrackingDataError,
      seoDataError
    },
    pageTrackingData: pageTrackingDataResponse,
    showPriceIndicator,
    videoPlay,
    videoTime,
    seller: {...seller, isSellerBlocked},
    showB2cFinancingPanel,
    showC2cFinancing,
    tagclouds: [stockTagclouds, wdylTagclouds].filter(Boolean),
    targetBuyer,
    vehicleCertification,
    vehicleHistory,
    vehicleInfo,
    groupedSpecifications,
    ...(Boolean(isTradable) && {isTradableAd: isTradable.value}),
    ...(Boolean(recommendedAds) && {relatedAds: withFavoriteFlag(recommendedAds.value)})
  }
}
